import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * Shows or hides the certification popup trigger per variant.
 * Uses a variant ID → boolean map rendered in Liquid from
 * custom.enable_product_certification_popup (strict .value == true).
 */
class CertificationPopupRoot extends HTMLElement {
  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);

    if (this.dataset.designMode !== 'true') {
      this.#syncFromSelectedVariant();
    }
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  /**
   * @returns {Record<string, boolean>}
   */
  #getVariantMap() {
    const mapScript = this.querySelector('[data-certification-variant-map]');
    if (!mapScript?.textContent) return {};

    try {
      return JSON.parse(mapScript.textContent);
    } catch {
      return {};
    }
  }

  /**
   * @param {Document} html
   */
  #updateVariantMapFromHtml(html) {
    const popupId = this.dataset.certificationPopup;
    const newRoot = html.querySelector(
      `[data-certification-popup-root][data-certification-popup="${popupId}"]`
    );
    const newMapScript = newRoot?.querySelector('[data-certification-variant-map]');
    const currentMapScript = this.querySelector('[data-certification-variant-map]');

    if (newMapScript?.textContent && currentMapScript) {
      currentMapScript.textContent = newMapScript.textContent;
    }

    if (newRoot?.dataset.productId) {
      this.dataset.productId = newRoot.dataset.productId;
    }
  }

  /**
   * @returns {string | null}
   */
  #getSelectedVariantId() {
    const section = this.closest('.shopify-section, dialog');
    const variantInput = section?.querySelector(
      'form[action*="/cart/add"] input[name="id"], product-form input[name="id"]'
    );

    if (variantInput?.value) return variantInput.value;

    return new URLSearchParams(window.location.search).get('variant');
  }

  /**
   * @param {string | number | null | undefined} variantId
   * @returns {boolean}
   */
  #isEnabledForVariant(variantId) {
    if (!variantId) return false;

    const map = this.#getVariantMap();
    return map[String(variantId)] === true;
  }

  /**
   * @param {boolean} isVisible
   */
  #setVisible(isVisible) {
    if (isVisible) {
      this.removeAttribute('hidden');
    } else {
      this.setAttribute('hidden', '');
      this.querySelector('dialog-component')?.closeDialog?.();
    }
  }

  #syncFromSelectedVariant() {
    this.#setVisible(this.#isEnabledForVariant(this.#getSelectedVariantId()));
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (this.dataset.designMode === 'true') return;

    if (event.detail.data.newProduct) {
      this.#updateVariantMapFromHtml(event.detail.data.html);
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const variantId = event.detail.resource?.id;
    if (!variantId) return;

    this.#setVisible(this.#isEnabledForVariant(variantId));
  };
}

if (!customElements.get('certification-popup-root')) {
  customElements.define('certification-popup-root', CertificationPopupRoot);
}
